import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { AtomsToastVariant } from '@opendoor/atoms/toast';
import { useAtomsToastManager } from '@opendoor/atoms/toast';

export type ToastType = 'info' | 'error' | 'success';

export type ShowToastOptions = {
  /**
   * Visual treatment: `success` green, `error` red, `info` brand blue. Also sets
   * the leading icon.
   */
  type: ToastType;

  /**
   * Text shown in the toast, truncated at 500 characters.
   */
  message: string;

  /**
   * How long the toast stays up before auto-dismissing, in ms. Clamped to
   * 1,000–30,000; a non-finite value falls back to the default.
   *
   * Default 3,000 ms
   */
  autoCloseMs?: number;
};

interface ToastContextValue {
  /**
   * Queues a toast in the app-level `<Toasty>` viewport. Returns the toast id to
   * pass to `hideToast`, or `undefined` when the message was rejected.
   */
  showToast: (options: ShowToastOptions) => string | undefined;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const MIN_DURATION = 1000; // 1 second minimum
const MAX_DURATION = 30000; // 30 seconds maximum
const DEFAULT_DURATION = 3000; // 3 seconds default
const MAX_MESSAGE_LENGTH = 500; // Prevent extremely long messages

const VARIANT_BY_TYPE: Record<ToastType, AtomsToastVariant> = {
  info: 'info',
  error: 'error',
  success: 'success',
};

/**
 * ToastProvider exposes app-wide toast helpers over the Atoms toast manager.
 *
 * Must be rendered under `AtomsAppProvider`, which mounts the `<Toasty>`
 * viewport that owns the manager. The viewport handles stacking, the
 * three-at-a-time cap, hover-to-pause, and swipe-to-dismiss.
 *
 * @example
 * ```tsx
 * // Wrap your app
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Use in any component
 * const { showToast } = useToast();
 * showToast({ type: 'success', message: 'Saved successfully!' });
 * ```
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  // The manager identity changes each render; read it through a ref so the
  // returned callbacks stay stable (call sites pass `showToast` into effect deps).
  // The ref is read at call time (post-commit), so syncing it in an effect rather
  // than during render is safe.
  const manager = useAtomsToastManager();
  const managerRef = useRef(manager);
  useEffect(() => {
    managerRef.current = manager;
  });

  const showToast = useCallback(
    ({ type, message, autoCloseMs = DEFAULT_DURATION }: ShowToastOptions) => {
      if (typeof message !== 'string') {
        console.warn('[Toast] Message must be a string');
        return undefined;
      }

      const safeMessage = message.slice(0, MAX_MESSAGE_LENGTH);
      if (message.length > MAX_MESSAGE_LENGTH) {
        console.warn(
          `[Toast] Message truncated from ${message.length} to ${MAX_MESSAGE_LENGTH} chars`,
        );
      }

      // A non-finite duration would clamp to NaN, which the viewport reads as "no
      // timer" and leaves the toast up forever.
      const safeDuration = Number.isFinite(autoCloseMs)
        ? Math.min(Math.max(autoCloseMs, MIN_DURATION), MAX_DURATION)
        : DEFAULT_DURATION;

      return managerRef.current.add({
        title: safeMessage,
        variant: VARIANT_BY_TYPE[type],
        timeout: safeDuration,
      });
    },
    [],
  );

  const hideToast = useCallback((id: string) => {
    managerRef.current.close(id);
  }, []);

  const contextValue = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>;
};

/**
 * Hook to access toast functionality from the ToastProvider.
 *
 * @throws {Error} If used outside of ToastProvider
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { showToast } = useToast();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       showToast({ type: 'success', message: 'Data saved successfully!' });
 *     } catch (error) {
 *       showToast({ type: 'error', message: 'Failed to save data' });
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * };
 * ```
 */
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
